
import { motion } from "framer-motion";
import { Hammer, Palette, Home, Droplet, Leaf, BedDouble, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import Image from "@/components/ui/image";

export const RenovationExamplesSection = () => {
  const { toast } = useToast();
  const [hasShownToast, setHasShownToast] = useState(false);
  
  // Show toast after 10 seconds of reading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasShownToast) {
        toast({
          title: "Remonttisuunnitelmia?",
          description: "Saat kilpailukykyisen tarjouksen remonttirahoitukseen helposti.",
          action: (
            <Link 
              to="/kilpailuta" 
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Kilpailuta laina
            </Link>
          ),
        });
        setHasShownToast(true);
      }
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [toast, hasShownToast]);

  // Using reliable images specifically for renovations with direct URLs
  const renovationExamples = [
    { 
      icon: <Palette className="h-8 w-8 md:h-10 md:w-10 text-blue-400" />,
      title: "Sisätilojen pintaremontti", 
      description: "Maalaus, tapetointi ja lattian uusiminen",
      articlePath: "/artikkelit/remontointi-vinkkeja",
      imageSrc: "https://images.unsplash.com/photo-1615875605825-5eb9bb5d52ac?auto=format&fit=crop&w=800&q=80"
    },
    { 
      icon: <Droplet className="h-8 w-8 md:h-10 md:w-10 text-blue-400" />,
      title: "Kylpyhuone ja sauna", 
      description: "Vesikalusteet, laatoitus ja kosteuseristys",
      articlePath: "/artikkelit/kylpyhuone-remontti",
      imageSrc: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=800&q=80"
    },
    { 
      icon: <Home className="h-8 w-8 md:h-10 md:w-10 text-blue-400" />,
      title: "Keittiöremontti", 
      description: "Kaapit, tasot ja kodinkoneet",
      articlePath: "/artikkelit/keittio-remontti",
      imageSrc: "https://images.unsplash.com/photo-1556911220-bff31c812dba?auto=format&fit=crop&w=800&q=80"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-8 md:mb-12"
    >
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 md:p-8 border border-blue-100">
        <h2 className="text-xl md:text-2xl font-bold text-blue-700 mb-4 md:mb-6 flex items-center">
          <Hammer className="mr-2 text-blue-500" />
          Suosituimmat remonttikohteet
        </h2>
        
        <p className="text-sm md:text-base text-gray-700 mb-4 md:mb-6">
          Remonttilaina sopii monenlaisiin kodin kunnostushankkeisiin. Olipa kyseessä pieni pintaremontti 
          tai isompi saneeraus, joustava rahoitus mahdollistaa kodin parantamisen toiveidesi mukaan.
          Tutustu alla esimerkkeihin suosituimmista remonttikohteista.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          {renovationExamples.map((item, index) => (
            <Link 
              key={index} 
              to={item.articlePath}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow group relative"
              onClick={() => {
                // Show toast after clicking an item if not shown before
                if (!hasShownToast) {
                  setTimeout(() => {
                    toast({
                      title: "Remontin rahoitus",
                      description: "Rahoita kotisi kunnostaminen kilpailukykyisellä lainalla.",
                      action: (
                        <Link 
                          to="/kilpailuta" 
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Hae lainaa
                        </Link>
                      ),
                    });
                  }, 5000);
                  setHasShownToast(true);
                }
              }}
            >
              <div className="relative">
                <AspectRatio ratio={16/9}>
                  <div className="w-full h-full bg-gray-200 animate-pulse absolute z-0"></div>
                  <Image
                    src={item.imageSrc}
                    alt={item.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 relative z-10"
                    fallbackSrc="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?auto=format&fit=crop&w=800&q=80"
                    priority={index === 0}
                    objectFit="cover"
                  />
                </AspectRatio>
                {/* Overlay with gradient to make text visible */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-70 z-20"></div>
                {/* Position title and description at the bottom of the image */}
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white z-30">
                  <div className="flex items-center gap-2 mb-1">
                    {item.icon}
                    <h3 className="font-semibold text-sm md:text-base text-white">{item.title}</h3>
                  </div>
                  <p className="text-white/90 text-xs md:text-sm mb-1 md:mb-2">{item.description}</p>
                  <div className="flex items-center text-blue-200 text-xs md:text-sm font-medium">
                    Lue lisää <ArrowRight className="ml-1 h-3 w-3 md:h-4 md:w-4" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-blue-100">
          <h3 className="text-base md:text-lg font-semibold text-blue-700 mb-2 md:mb-3">Miksi valita juuri remonttilaina?</h3>
          <ul className="list-disc list-inside space-y-1 md:space-y-2 text-xs md:text-sm text-gray-700 mb-3 md:mb-4">
            <li>Kilpailukykyinen korko muihin rahoitusmuotoihin verrattuna</li>
            <li>Mahdollisuus isompaan lainasummaan remonttisuunnitelman mukaan</li>
            <li>Selkeä maksusuunnitelma, joka sopii talouteesi</li>
            <li>Nopea päätös - saat rahoituksen suunnittelun alkuvaiheessa</li>
            <li>Mahdollisuus parantaa asuntosi arvoa ja asumisviihtyvyyttä</li>
          </ul>
          
          <Link 
            to="/kilpailuta" 
            className="inline-flex items-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 md:px-6 py-2 md:py-3 rounded-md text-sm md:text-base font-medium transition-colors"
          >
            Hae rahoitusta remonttiprojektiisi
            <Hammer className="ml-2 h-4 w-4 md:h-5 md:w-5" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};
